// Official IA guided interview question set for phone intake.
// Order of use: IDENTIFICATION -> UNIVERSAL (every complaint) -> per-allegation -> CLOSING.

export const IDENTIFICATION_QUESTIONS = [
  "Please state your full legal name and preferred name.",
  "What are your preferred pronouns?",
  "What is your date of birth?",
  "What is your address, telephone number, and email address?",
  "What is your preferred method and time of contact?",
  "Do you require an interpreter, disability accommodation, or other assistance?",
  "Are you filing this complaint for yourself or on behalf of someone else?",
  "If filing for someone else, what is your relationship to that person?",
  "Do you wish to remain confidential to the extent permitted by law and policy?",
  "Are you concerned about retaliation or your immediate safety?",
  "Have you previously reported this matter? If so, to whom and when?",
  "How did you learn about the complaint process?",
];

export const UNIVERSAL_QUESTIONS = [
  "Please describe what happened from beginning to end in your own words.",
  "What date and approximate time did the incident occur?",
  "Where exactly did it occur?",
  "What first brought you into contact with the employee or officer?",
  "Which employees or officers were involved?",
  "Do you know their names, badge numbers, unit numbers, assignments, or physical descriptions?",
  "What did each person say or do?",
  "What did you say or do?",
  "In what order did the events occur?",
  "How long did the encounter last?",
  "What could you personally see and hear?",
  "Was anything obstructing your view or affecting your ability to hear?",
  "Were lighting, weather, distance, noise, medication, alcohol, injury, or stress factors?",
  "Were there witnesses? Provide their names and contact information.",
  "Did anyone record the incident?",
  "Are surveillance cameras, body-worn cameras, vehicle cameras, dispatch recordings, or access-control records likely available?",
  "Do you have photographs, videos, messages, documents, medical records, receipts, or other evidence?",
  "Did you suffer any injury, financial loss, property damage, emotional distress, or other harm?",
  "Did you seek medical care or other assistance?",
  "What actions do you believe were improper?",
  "What would you like the agency to understand or address?",
  "Is there anything else that may help establish what happened?",
];

export const CLOSING_QUESTIONS = [
  "Have we accurately captured your allegations?",
  "Is there anything you want to correct or clarify?",
  "Are there additional incidents involving the same employee?",
  "Are there other witnesses we should contact?",
  "Is there additional evidence you need time to obtain?",
  "Have you provided everything currently in your possession?",
  "Has anyone threatened, pressured, coached, or discouraged you concerning this complaint?",
  "Are you concerned about retaliation after this interview?",
  "May we contact you if additional questions arise?",
  "What is the best way to provide you with status notifications?",
  "Do you understand that the allegation will be evaluated based on the available evidence?",
  "Do you certify that your statement is complete and accurate to the best of your knowledge?",
];

// Investigator guidance notes shown under specific categories.
export const CATEGORY_NOTES = {
  "Sexual Misconduct":
    "Investigators should use a trauma-informed approach, avoid unnecessary repetition, and coordinate with criminal investigators when required.",
  "Criminal Conduct":
    "IA should stop or carefully limit questioning when necessary to preserve a parallel criminal investigation and coordinate with the appropriate investigative authority.",
};

// Keys MUST match COMPLAINT_CATEGORIES in backend routes_public.py exactly.
export const CATEGORY_QUESTIONS = {
  "Unprofessional Conduct": [
    "What specific conduct did you consider unprofessional?",
    "What words, gestures, expressions, or actions were used?",
    "What was the employee's tone and demeanor?",
    "Was the behavior directed at you, another person, or the public generally?",
    "Did the employee explain their actions or decisions?",
    "Did you ask to speak with a supervisor? What happened?",
    "How did the conduct affect the encounter or your ability to receive assistance?",
    "Were other employees present, and how did they respond?",
  ],
  "Discourtesy": [
    "What exactly did the employee say or do that you considered discourteous?",
    "Were insulting, sarcastic, profane, dismissive, or threatening words used?",
    "Were you interrupted, ignored, mocked, or refused an opportunity to explain?",
    "Did the employee shout or use an inappropriate tone?",
    "Did the employee make any gestures or facial expressions you considered disrespectful?",
    "Did the employee eventually provide the requested service or explanation?",
    "Were any witnesses close enough to hear the conversation?",
  ],
  "Harassment": [
    "What conduct do you believe constituted harassment?",
    "Was the conduct verbal, physical, written, electronic, or repeated surveillance?",
    "How many times did it occur?",
    "When and where did each incident occur?",
    "Did the same employee participate each time?",
    "Did you tell the employee the conduct was unwanted or ask them to stop?",
    "Were threats, intimidation, insults, unwanted contact, or repeated communications involved?",
    "Do you have messages, call records, screenshots, recordings, or witnesses?",
    "Has the employee contacted you since the reported incident?",
    "Are you currently concerned for your safety?",
  ],
  "Excessive Force": [
    "What happened immediately before force was used?",
    "What type of force was used?",
    "Which employee used each type of force?",
    "Where on your body was force applied?",
    "Were you struck, pushed, restrained, sprayed, shocked, taken to the ground, or subjected to another device?",
    "How many times was force used, to the best of your recollection?",
    "What commands or warnings did you hear?",
    "Were you physically resisting, attempting to leave, or complying?",
    "Did you tell the employee that you were injured, pregnant, disabled, or experiencing a medical emergency?",
    "When did the force stop?",
    "Were you handcuffed, restrained, or already under control when force was used?",
    "What injuries resulted?",
    "Were photographs taken or medical services requested?",
    "What clothing or physical evidence still exists?",
    "Were body-worn cameras, security cameras, or witnesses present?",
  ],
  "Improper Search or Seizure": [
    "What person, vehicle, home, device, bag, or property was searched?",
    "Who conducted or directed the search?",
    "What reason did the employee provide?",
    "Did the employee ask for consent?",
    "What exactly did you say in response?",
    "Did the employee present or mention a warrant?",
    "Were you told that you could refuse the search?",
    "Were any doors, containers, locked areas, electronic devices, or private records accessed?",
    "What property was taken?",
    "Did you receive a property receipt, inventory, warrant copy, or case number?",
    "Was any property damaged, lost, or not returned?",
    "Who owned or controlled the searched location or property?",
  ],
  "False Arrest / Improper Detention": [
    "When did you first believe you were not free to leave?",
    "What did the employee say about your detention or arrest?",
    "Did you ask whether you were free to leave? What was the response?",
    "How long were you detained?",
    "Were you handcuffed, transported, searched, questioned, or placed in a vehicle or holding area?",
    "What reason was given for the detention or arrest?",
    "Were you told what offense you were suspected of committing?",
    "Were you issued a citation, charging document, or release paperwork?",
    "Were the charges later declined, dismissed, or changed?",
    "Did you provide identification or information that you believe should have resolved the matter?",
    "Were any witnesses able to confirm your identity, location, or conduct?",
    "What harm resulted from the detention or arrest?",
  ],
  "Discrimination / Bias-Based Conduct": [
    "What makes you believe bias or discrimination influenced the employee's conduct?",
    "What protected or perceived characteristic do you believe was involved?",
    "Were specific words, slurs, comments, questions, or gestures used?",
    "When were those statements made, and who heard them?",
    "Were similarly situated people treated differently?",
    "Please describe the comparison and how their treatment differed.",
    "Did the employee make assumptions about your identity, status, neighborhood, or background?",
    "Did you report the perceived bias to a supervisor?",
    "Have you experienced similar conduct involving this employee or agency?",
    "Is there documentation, video, or witness information supporting your concern?",
  ],
  "Retaliation": [
    "What earlier complaint, report, testimony, protected activity, or cooperation do you believe led to retaliation?",
    "Who knew about that activity?",
    "What retaliatory action occurred?",
    "When did it occur in relation to your earlier activity?",
    "What statements or actions connect the two events?",
    "Were you threatened, targeted, denied service, disciplined, cited, arrested, reassigned, or otherwise disadvantaged?",
    "Did the conduct occur more than once?",
    "Did you report the retaliation? To whom?",
    "Are you still experiencing retaliation?",
    "What protections or immediate action do you believe are necessary?",
  ],
  "Failure to Provide Service": [
    "What service or assistance did you request?",
    "How and when did you make the request?",
    "Which employee received it?",
    "Was a call, incident, request, or case number created?",
    "What response were you given?",
    "Did anyone explain why service could not be provided?",
    "Was the request referred to another employee or agency?",
    "How long did you wait?",
    "Did you make follow-up requests?",
    "Was there an immediate safety or medical concern?",
    "What happened because the service was delayed or refused?",
  ],
  "Neglect of Duty": [
    "What duty do you believe the employee failed to perform?",
    "How did you become aware of the failure?",
    "What did the employee know at the time?",
    "What action should have been taken?",
    "Did the employee acknowledge the responsibility?",
    "Was the employee asked or directed to act?",
    "Were there warnings, reports, or prior opportunities to address the issue?",
    "Did another employee eventually perform the duty?",
    "What harm or risk resulted?",
    "Are there reports, logs, messages, or witnesses supporting the allegation?",
  ],
  "Improper Driving / Vehicle Operation": [
    "Where and when did the driving conduct occur?",
    "What vehicle was involved?",
    "Do you know the vehicle, unit, tag, or fleet number?",
    "What direction was the vehicle traveling?",
    "What specific driving behavior concerned you?",
    "Were emergency lights or sirens activated?",
    "What were the road, traffic, lighting, and weather conditions?",
    "Approximately how fast was the vehicle traveling?",
    "Did the vehicle strike anything or cause another driver or pedestrian to take evasive action?",
    "Were you able to observe who was driving?",
    "Is dash-camera, traffic-camera, GPS, or surveillance footage likely available?",
    "Were injuries or property damage reported?",
  ],
  "Improper Use of Authority": [
    "What authority did the employee claim or exercise?",
    "What did the employee order, demand, threaten, or require?",
    "Was the conduct related to an official agency purpose?",
    "Did the employee identify themselves or display credentials?",
    "Did the employee seek a personal, financial, political, or other benefit?",
    "Did you feel compelled to comply? Why?",
    "What consequence did the employee threaten or imply?",
    "Were agency resources, records, equipment, or status used?",
    "Were other employees or witnesses present?",
    "What documents or communications support the allegation?",
  ],
  "Policy or Procedure Violation": [
    "What action or omission do you believe violated policy?",
    "How did you learn about the applicable policy or procedure?",
    "Do you know the policy name, number, or requirement?",
    "What should the employee have done differently?",
    "Was a supervisor present or notified?",
    "Did the employee explain why a different procedure was followed?",
    "Was this an isolated event or part of a repeated practice?",
    "What records, training materials, notices, or witnesses support your concern?",
  ],
  "Dishonesty / False Statement": [
    "What statement do you believe was false or misleading?",
    "Who made the statement?",
    "When, where, and to whom was it made?",
    "Was the statement oral, written, electronic, or included in an official report?",
    "What evidence shows that it was inaccurate?",
    "Do you believe the error was intentional? What supports that belief?",
    "Was important information omitted?",
    "Did you give the employee information that was later reported differently?",
    "Who else heard or reviewed the statement?",
    "Was the statement later corrected or repeated?",
  ],
  "Theft / Property Misconduct": [
    "What property is missing, damaged, misused, or improperly retained?",
    "Who owns the property?",
    "When and where was it last seen?",
    "Which employee handled or had access to it?",
    "What is its description, serial number, and approximate value?",
    "Was the property inventoried, seized, stored, transferred, or returned?",
    "Did you receive a receipt or property record?",
    "Were any seals, containers, lockers, or storage areas involved?",
    "Who else had access?",
    "Do photographs, receipts, tracking data, video, or witnesses exist?",
  ],
  "Corruption / Bribery": [
    "What benefit, payment, gift, favor, or special treatment was offered, requested, accepted, or exchanged?",
    "Who made the offer or request?",
    "What was expected in return?",
    "When and where did this occur?",
    "What exact words were used?",
    "Was money transferred? If so, how and in what amount?",
    "Were intermediaries or other employees involved?",
    "Are there messages, bank records, receipts, recordings, or witnesses?",
    "Has this occurred on other occasions?",
    "Were official actions, contracts, investigations, citations, or enforcement decisions affected?",
    "Is any evidence at risk of being destroyed or moved?",
  ],
  "Sexual Misconduct": [
    "Please describe what occurred in your own words and at your own pace.",
    "What did the employee say or do?",
    "Was the conduct verbal, physical, visual, written, or electronic?",
    "Was the contact or communication unwanted?",
    "Did the employee request sexual contact, images, dates, or favors?",
    "Did the employee use or reference their authority, position, weapon, or ability to affect your situation?",
    "Were threats, promises, coercion, retaliation, or special treatment involved?",
    "Where and when did each incident occur?",
    "Were there witnesses or people you told shortly afterward?",
    "Are messages, photographs, recordings, clothing, medical evidence, or other materials available?",
    "Has the employee contacted you since the incident?",
    "Do you need immediate medical care, advocacy, safety planning, or another support service?",
  ],
  "Improper Communication / Language": [
    "What exact words or phrases do you remember?",
    "Who made the statement, and to whom was it directed?",
    "What was the context of the communication?",
    "Was it spoken, written, broadcast by radio, posted online, or sent electronically?",
    "What was the employee's tone?",
    "Did the communication include profanity, threats, insults, slurs, ridicule, or confidential information?",
    "Who heard or received it?",
    "Is a recording, screenshot, email, message, or dispatch log available?",
    "Did the employee later explain, correct, or apologize for the communication?",
  ],
  "Improper Handling of Evidence or Property": [
    "What evidence or property was involved?",
    "How was it collected, packaged, labeled, transported, stored, or released?",
    "Which employees handled it?",
    "What makes you believe it was lost, altered, contaminated, damaged, substituted, or improperly accessed?",
    "Were evidence seals or containers damaged or open?",
    "Did the documented description match the actual property?",
    "Were receipts, chain-of-custody forms, photographs, or inventory records created?",
    "When was the discrepancy discovered?",
    "Who had access during the relevant period?",
    "Could the handling affect a criminal, civil, or administrative case?",
  ],
  "Criminal Conduct": [
    "What specific conduct do you believe may constitute a crime?",
    "Who committed or participated in the conduct?",
    "When and where did it occur?",
    "What did you personally observe?",
    "What information did you receive from someone else?",
    "Were threats, weapons, injuries, financial transactions, or stolen property involved?",
    "Is there physical, digital, documentary, or forensic evidence?",
    "Who are the witnesses?",
    "Has the matter been reported to a law-enforcement or prosecutorial agency?",
    "Is anyone currently in danger?",
    "Is any evidence at risk of destruction?",
    "Has anyone asked you to conceal, alter, or withdraw information?",
  ],
  "Conflict of Interest": [
    "What relationship or interest do you believe created the conflict?",
    "Who was involved in that relationship?",
    "Was it personal, family-related, romantic, financial, political, or business-related?",
    "What official decision or action may have been affected?",
    "Did the employee disclose the relationship or interest?",
    "Did the employee participate in a matter from which they should have been separated?",
    "Was preferential treatment or unfavorable treatment provided?",
    "What records or witnesses show the connection?",
    "Did the employee or someone associated with them receive a benefit?",
  ],
  "Improper Disclosure of Confidential Information": [
    "What information was disclosed?",
    "Why do you believe the information was confidential or restricted?",
    "Who had authorized access to it?",
    "Who disclosed it, and to whom?",
    "When and how was it disclosed?",
    "Was it communicated verbally, in writing, electronically, through social media, or through records access?",
    "How did you learn about the disclosure?",
    "Are screenshots, emails, messages, access logs, or witnesses available?",
    "Was the information used to intimidate, embarrass, retaliate against, or benefit anyone?",
    "What harm or risk resulted from the disclosure?",
  ],
  "Social Media Misconduct": [
    "Which social-media platform and account were involved?",
    "What was posted, shared, liked, transmitted, or commented?",
    "What is the account name or profile address?",
    "When did you observe the content?",
    "Why do you believe the account belongs to the employee?",
    "Did the profile identify the employee's agency, title, uniform, badge, equipment, or workplace?",
    "Did the content include threats, discrimination, harassment, confidential information, or evidence?",
    "Was the content public, private, temporary, or later deleted?",
    "Did you preserve screenshots, links, screen recordings, or metadata?",
    "Who else saw or received the content?",
    "Did the employee contact you online before or after the incident?",
  ],
  "Failure to Identify / Provide Unit Information": [
    "Did you ask the employee to identify themselves?",
    "What exactly did you request: name, badge number, employee number, unit number, or supervisor?",
    "How did the employee respond?",
    "Was the employee in uniform or plain clothes?",
    "Were their nameplate, badge, agency markings, or vehicle number visible?",
    "Did the employee cover, remove, or prevent you from viewing identifying information?",
    "Did the employee provide information you believe was false or incomplete?",
    "Were other employees present who could identify the employee?",
    "Do you have photographs, video, dispatch information, a citation, or a case number?",
  ],
  "Other": [
    "Please explain the concern in your own words.",
    "Why do you believe the conduct was improper?",
    "Who was involved?",
    "When and where did it occur?",
    "What did you personally observe?",
    "What outcome or harm resulted?",
    "Are there witnesses or supporting evidence?",
    "Does the concern involve a continuing practice or immediate risk?",
    "Is there another category that may also apply?",
    "What additional information should the agency consider?",
  ],
};
